/**
 * Type-safe Statsig hooks.
 *
 * These re-export the SDK hooks with narrowed types from the generated gate/experiment
 * names. Consumers import from @coursera-web/statsig (ESLint blocks direct
 * @statsig/react-bindings imports), so all Statsig usage goes through these
 * typed re-exports automatically.
 */
import type {
  ExperimentEvaluationOptions,
  FeatureGateEvaluationOptions,
  LayerEvaluationOptions,
} from '@statsig/client-core';
import {
  useExperiment as useExperimentRaw,
  useFeatureGate as useFeatureGateRaw,
  useGateValue as useGateValueRaw,
  useLayer as useLayerRaw,
  useStatsigClient as useStatsigClientRaw,
} from '@statsig/react-bindings';

import type { StatsigExperimentName, StatsigExperimentParams } from '../__generated__/experiments';
import type { StatsigGateName } from '../__generated__/gates';
import type { StatsigLayerName, StatsigLayerParams } from '../__generated__/layers';

type WithoutDisableExposureLog<T> = Omit<NonNullable<T>, 'disableExposureLog'> & {
  disableExposureLog?: never;
};

export type StatsigFeatureGateEvaluationOptions = WithoutDisableExposureLog<FeatureGateEvaluationOptions>;
export type StatsigExperimentEvaluationOptions = WithoutDisableExposureLog<ExperimentEvaluationOptions>;
export type StatsigLayerEvaluationOptions = WithoutDisableExposureLog<LayerEvaluationOptions>;

type RawStatsigClient = ReturnType<typeof useStatsigClientRaw>;
type RawStatsigEvaluationClient = RawStatsigClient['client'];

type StatsigEvaluationClient = Omit<
  RawStatsigEvaluationClient,
  'checkGate' | 'getFeatureGate' | 'getExperiment' | 'getLayer' | 'getDynamicConfig' | 'getParameterStore'
> & {
  checkGate(
    gateName: Parameters<RawStatsigEvaluationClient['checkGate']>[0],
    options?: WithoutDisableExposureLog<Parameters<RawStatsigEvaluationClient['checkGate']>[1]>
  ): ReturnType<RawStatsigEvaluationClient['checkGate']>;
  getFeatureGate(
    gateName: Parameters<RawStatsigEvaluationClient['getFeatureGate']>[0],
    options?: WithoutDisableExposureLog<Parameters<RawStatsigEvaluationClient['getFeatureGate']>[1]>
  ): ReturnType<RawStatsigEvaluationClient['getFeatureGate']>;
  getExperiment(
    experimentName: Parameters<RawStatsigEvaluationClient['getExperiment']>[0],
    options?: WithoutDisableExposureLog<Parameters<RawStatsigEvaluationClient['getExperiment']>[1]>
  ): ReturnType<RawStatsigEvaluationClient['getExperiment']>;
  getLayer(
    layerName: Parameters<RawStatsigEvaluationClient['getLayer']>[0],
    options?: WithoutDisableExposureLog<Parameters<RawStatsigEvaluationClient['getLayer']>[1]>
  ): ReturnType<RawStatsigEvaluationClient['getLayer']>;
  getDynamicConfig(
    configName: Parameters<RawStatsigEvaluationClient['getDynamicConfig']>[0],
    options?: WithoutDisableExposureLog<Parameters<RawStatsigEvaluationClient['getDynamicConfig']>[1]>
  ): ReturnType<RawStatsigEvaluationClient['getDynamicConfig']>;
  getParameterStore(
    storeName: Parameters<RawStatsigEvaluationClient['getParameterStore']>[0],
    options?: WithoutDisableExposureLog<Parameters<RawStatsigEvaluationClient['getParameterStore']>[1]>
  ): ReturnType<RawStatsigEvaluationClient['getParameterStore']>;
};

export type StatsigClient = Omit<
  RawStatsigClient,
  'checkGate' | 'getFeatureGate' | 'getExperiment' | 'getLayer' | 'getDynamicConfig' | 'getParameterStore' | 'client'
> &
  Pick<
    StatsigEvaluationClient,
    'checkGate' | 'getFeatureGate' | 'getExperiment' | 'getLayer' | 'getDynamicConfig' | 'getParameterStore'
  > & {
    client: StatsigEvaluationClient;
  };

/**
 * Resolves the parameter types for a given experiment.
 * If the experiment has params defined in the Console, returns those typed params.
 * Otherwise falls back to Record<string, unknown>.
 */
type ParamsFor<N extends StatsigExperimentName> = N extends keyof StatsigExperimentParams
  ? StatsigExperimentParams[N]
  : Record<string, unknown>;

/**
 * Type-safe useGateValue. Only accepts known gate names.
 *
 * Usage:
 *   const isEnabled = useGateValue('simple_client_gate');
 */
export function useGateValue(gateName: StatsigGateName, options?: StatsigFeatureGateEvaluationOptions): boolean {
  return useGateValueRaw(gateName, options);
}

/**
 * Type-safe useFeatureGate. Only accepts known gate names.
 *
 * Usage:
 *   const gate = useFeatureGate('simple_client_gate');
 */
export function useFeatureGate(gateName: StatsigGateName, options?: StatsigFeatureGateEvaluationOptions) {
  return useFeatureGateRaw(gateName, options);
}

/**
 * Type-safe useExperiment. Only accepts known experiment names.
 *
 * The returned .get() enforces valid parameter names and correct types
 * based on the experiment's parameters defined in Statsig Console.
 *
 * Usage:
 *   const experiment = useExperiment('ohazda-test-experiment');
 *   const variant = experiment.get('variant', 'A');   // ✓
 *   const typo = experiment.get('typo', 'A');          // ✗ TS error
 */
export function useExperiment<N extends StatsigExperimentName>(
  experimentName: N,
  options?: StatsigExperimentEvaluationOptions
) {
  const experiment = useExperimentRaw(experimentName, options);

  return {
    ...experiment,
    get<K extends keyof ParamsFor<N> & string>(paramName: K, fallback: ParamsFor<N>[K]): ParamsFor<N>[K] {
      return experiment.get(paramName, fallback) as ParamsFor<N>[K];
    },
  };
}

/**
 * Resolves the parameter types for a given layer.
 * If the layer has params defined in the Console, returns those typed params.
 * Otherwise falls back to Record<string, unknown>.
 */
type LayerParamsFor<N extends StatsigLayerName> = N extends keyof StatsigLayerParams
  ? StatsigLayerParams[N]
  : Record<string, unknown>;

/**
 * Type-safe useLayer. Only accepts known layer names.
 *
 * The returned .get() enforces valid parameter names and correct types
 * based on the layer's parameters defined in Statsig Console.
 *
 * Usage:
 *   const layer = useLayer('my_layer');
 *   const color = layer.get('color', 'blue');   // ✓
 *   const typo = layer.get('typo', 'blue');      // ✗ TS error
 */
export function useLayer<N extends StatsigLayerName>(layerName: N, options?: StatsigLayerEvaluationOptions) {
  const layer = useLayerRaw(layerName, options);

  return {
    ...layer,
    get<K extends keyof LayerParamsFor<N> & string>(
      paramName: K,
      fallback: LayerParamsFor<N>[K]
    ): LayerParamsFor<N>[K] {
      return layer.get(paramName, fallback) as LayerParamsFor<N>[K];
    },
  };
}

export function useStatsigClient(): StatsigClient {
  return useStatsigClientRaw() as StatsigClient;
}
